import { TimeoutError } from '../error/timeout-error';

export type Awaitable<T> = T | Promise<T>;

const timeout = (timeoutInMs: number): [Promise<unknown>, () => void] => {
  let timer: NodeJS.Timeout;
  const cancel = () => {
    clearTimeout(timer);
  };

  const promise = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new TimeoutError());
    }, timeoutInMs);
  });

  return [promise, cancel];
};

export const withTimeout = async <T extends Promise<any>>(handler: T, timeoutInMs: number): Promise<Awaited<T>> => {
  const [promise, cancel] = timeout(timeoutInMs);
  try {
    return await Promise.race([handler, promise as Promise<T>]);
  } finally {
    cancel();
  }
};

/**
 * Retries a function until it succeeds or the maximum number of attempts is reached.
 *
 * @template T The type of the value returned by the handler function.
 * @param {() => Promise<T>} handler The function to retry.
 * @param {number} maxAttempts The maximum number of attempts to make.
 * @param {number} delayInMs The delay between attempts in milliseconds.
 * @returns {Promise<T>} The result of the successful function call.
 * @throws {Error} The last error thrown by the function if all attempts fail.
 *
 * @example
 * const result = await retry(() => fetchData(), 3, 1000);
 * // This will retry the fetchData function up to 3 times, waiting 1 second between attempts.
 * // If all attempts fail, the last error will be thrown.
 */
export const retry = async <T>(handler: () => Promise<T>, maxAttempts: number, delayInMs: number): Promise<T> => {
  let attempts = 0;
  let lastError: Error | undefined;

  do {
    try {
      return await handler();
    } catch (error) {
      lastError = error as Error;
      attempts++;
      if (attempts < maxAttempts) {
        await sleep(delayInMs);
      }
    }
  } while (attempts < maxAttempts);

  throw lastError;
};

/**
 * Logs the reasons for rejected promises in an array of PromiseSettledResult objects.
 */
export const logRejectedErrors =
  (message = 'Rejected promise error:', logger = console.error) =>
  <T>(results: PromiseSettledResult<T>[]): PromiseSettledResult<T>[] => {
    for (const result of results) {
      if (result.status !== 'rejected') {
        continue;
      }

      logger(message);
      console.info(`Reason: ${result.reason}`);
    }

    return results;
  };

/**
 * * Accepts an object where all values are promises and returns an object with all promises resolved
 * @deprecated
 * @param {PromiseObject} promiseObject
 * @return {*}  {Promise<any>} object with resolved keys
 */
export const promiseAllKeys = async (promiseObject: {
  [key: string]: Promise<any>;
}): Promise<{ [key: string]: any }> => {
  const result: { [key: string]: any } = {};

  await Promise.all(
    Object.keys(promiseObject).map(async (promiseKey) => {
      result[promiseKey] = await promiseObject[promiseKey];
    })
  );

  return result;
};

/**
 * Delays the execution of code by a specified amount of time.
 *
 * @param {number} ms - The time in milliseconds to wait before resolving the promise.
 * @returns {Promise<void>} A promise that resolves after the specified delay.
 *
 * @example
 * // Delay execution for 1 second
 * async function withSleepAction() {
 *   console.log("Start");
 *   await sleep(1000);
 *   console.log("After 1 second");
 * }
 */
export const sleep = async (ms: number): Promise<void> => {
  await new Promise((resolve) => {
    setTimeout(resolve, Math.max(ms, 0));
  });
};

export const withMinSleep = async <T>(promise: T, ms: number): Promise<Awaited<T>> => {
  return await Promise.all([promise, sleep(ms)]).then(([value]) => value);
};

type LogRejectedOptions = {
  logger?: typeof console.info;
  title?: string;
};

/**
 * Logs any failures that happen when the promises are awaited.
 *
 * @param promises An array of promises to await on
 * @param options Options for the way the results are logged
 * @returns the results of the promises using Promise.allSettled
 */
export const withLogRejected = async <T>(
  promises: Array<Promise<T>>,
  options?: LogRejectedOptions
): Promise<Array<PromiseSettledResult<T>>> => {
  const results = await Promise.allSettled(promises);
  logRejectedPromises(results, options);
  return results;
};

const logRejectedPromises = (
  settledPromises: Array<PromiseSettledResult<any>>,
  options?: LogRejectedOptions
): Array<string> => {
  const rejectedPromises = settledPromises.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );

  if (rejectedPromises.length < 1) {
    return [];
  }
  const logger = options?.logger ?? console.info;

  logger(`${rejectedPromises.length} of ${settledPromises.length} ${options?.title ?? ''} promises were rejected.`);
  rejectedPromises.forEach((result) => {
    const originalIndex = settledPromises.indexOf(result);
    console.info(`${originalIndex}: ${result.reason}`);
  });

  return rejectedPromises.map((result) => result.reason);
};

export const fulfilledValues = <T>(results: Array<PromiseSettledResult<T>>): Array<T> =>
  results
    .filter((result): result is PromiseFulfilledResult<T> => result.status === 'fulfilled')
    .map((result) => result.value);

type BaseValue = string | number | symbol | boolean | object | null | undefined;

export type ResolvableValue<T extends BaseValue> = T | (() => T) | Promise<T> | (() => Promise<T>);

export const resolveValue = async <T extends BaseValue>(value: ResolvableValue<T>): Promise<T> => {
  if (typeof value === 'function') {
    return (await value()) as T;
  }
  if (value instanceof Promise) {
    return (await value) as T;
  }
  return value;
};

type Predicate<T> = (value: T, index: number, array: Array<T>) => Promise<boolean> | boolean;

/**
 * Asynchronously filters an array based on a predicate function that can return a boolean or a Promise<boolean>.
 *
 * @template T The type of elements in the input array.
 * @param {Array<T>} array - The array to filter.
 * @param {Predicate<T>} predicate - An async or sync function that returns true to keep the element, or false to exclude it.
 * @returns {Promise<T[]>} A promise that resolves to a new array with only the elements for which the predicate returned true.
 *
 * @example
 * // Filter out odd numbers asynchronously
 * const numbers = [1, 2, 3, 4];
 * const isEven = async (n: number) => n % 2 === 0;
 * const evens = await filterAsync(numbers, isEven); // [2, 4]
 */
export const filterAsync = async <T>(array: Array<T>, predicate: Predicate<T>): Promise<T[]> => {
  const results: Array<boolean> = await Promise.all(
    array.map(async (value, index) => await predicate(value, index, array))
  );
  return array.filter((_, i) => results[i]);
};

/**
 * Maps an array asynchronously with a maximum number of in-flight promises at a time.
 * Preserves input order in the returned array. Rejects on the first iteratee rejection.
 *
 * @template T The type of elements in the input array.
 * @template R The type of values produced by the iteratee.
 * @param {ReadonlyArray<T>} items The input array to map over.
 * @param {number} concurrency Maximum number of iteratee invocations in flight at once. Values below 1 are clamped to 1.
 * @param {(item: T, index: number) => Promise<R>} iteratee Async function applied to each item.
 * @returns {Promise<Array<R>>} A promise that resolves to a new array of mapped values, in input order.
 *
 * @example
 * // Fetch up to 5 records at a time
 * const records = await mapWithConcurrency(ids, 5, (id) => api.find(id));
 */
export const mapWithConcurrency = async <T, R>(
  items: ReadonlyArray<T>,
  concurrency: number,
  iteratee: (item: T, index: number) => Promise<R>
): Promise<Array<R>> => {
  if (items.length === 0) {
    return [];
  }
  const results: Array<R> = Array.from({ length: items.length });
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(concurrency, 1), items.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const i = nextIndex;
      nextIndex += 1;
      if (i >= items.length) {
        return;
      }
      results[i] = await iteratee(items[i] as T, i);
    }
  });
  await Promise.all(workers);
  return results;
};
